import { MediaKitData, MediaKitSocials } from '../services/mediaKitService';
import { normalizeAtHandleForStorage } from './socialMediaUtils';

export type MediaKitSocialFormState = {
    instagramHandle: string;
    tiktokHandle: string;
    linkedinUrl: string;
    facebookName: string;
    hudlUrl: string;
    xHandle: string;
};

export type MediaKitSocialFieldKey = keyof MediaKitSocialFormState;

export const MEDIA_KIT_SOCIAL_FIELDS: ReadonlyArray<{
    key: MediaKitSocialFieldKey;
    label: string;
    placeholder: string;
}> = [
    { key: 'instagramHandle', label: 'Instagram', placeholder: '@username' },
    { key: 'linkedinUrl', label: 'LinkedIn', placeholder: 'linkedin.com/in/username' },
    { key: 'hudlUrl', label: 'Hudl', placeholder: 'hudl.com/profile/123' },
    { key: 'tiktokHandle', label: 'TikTok', placeholder: '@username' },
    { key: 'facebookName', label: 'Facebook', placeholder: 'Your display name' },
    { key: 'xHandle', label: 'X', placeholder: '@username' },
];

export const emptyMediaKitSocialFormState = (): MediaKitSocialFormState => ({
    instagramHandle: '',
    tiktokHandle: '',
    linkedinUrl: '',
    facebookName: '',
    hudlUrl: '',
    xHandle: '',
});

export function mapSocialsToFormState(socials: MediaKitSocials): MediaKitSocialFormState {
    return {
        instagramHandle: normalizeAtHandleForStorage(socials.instagramHandle),
        tiktokHandle: normalizeAtHandleForStorage(socials.tiktokHandle),
        linkedinUrl: socials.linkedinUrl ?? '',
        facebookName: socials.facebookName ?? '',
        hudlUrl: socials.hudlUrl ?? '',
        xHandle: normalizeAtHandleForStorage(socials.xHandle),
    };
}

/** True when at least one social field has a non-blank value. */
export function hasMediaKitSocials(socials: MediaKitSocials | null | undefined): boolean {
    if (!socials) {
        return false;
    }
    return MEDIA_KIT_SOCIAL_FIELDS.some(({ key }) => {
        const value = socials[key];
        return typeof value === 'string' && value.trim().length > 0;
    });
}

/**
 * True when the athlete has saved any media-kit-specific progress beyond profile defaults.
 * Socials are excluded — they are often pre-populated from the athlete profile.
 * Used to skip the landing screen when returning to an incomplete wizard.
 */
export function hasMediaKitWizardProgress(kit: MediaKitData | null | undefined): boolean {
    if (!kit || kit.completed) {
        return false;
    }
    return Boolean(
        (kit.aboutYou && kit.aboutYou.trim()) ||
            (kit.photos && kit.photos.length > 0) ||
            (kit.selectedInterests && kit.selectedInterests.length > 0) ||
            (kit.customInterests && kit.customInterests.trim()) ||
            (kit.introVideoUrl && kit.introVideoUrl.trim()) ||
            (kit.highlights && kit.highlights.length > 0),
    );
}

/**
 * Best wizard step to resume for an incomplete kit (1–8). Returns 0 for landing / no progress.
 * Prefers the first unfinished content step so athletes can continue after leaving mid-flow.
 */
export function inferMediaKitWizardResumeStep(kit: MediaKitData | null | undefined): number {
    if (!kit || kit.completed || !hasMediaKitWizardProgress(kit)) {
        return 0;
    }
    if (!kit.aboutYou?.trim()) {
        return 2;
    }
    const hasLaterThanPhotos =
        (kit.selectedInterests?.length ?? 0) > 0 ||
        Boolean(kit.customInterests?.trim()) ||
        Boolean(kit.introVideoUrl?.trim()) ||
        (kit.highlights?.length ?? 0) > 0;
    if (!hasLaterThanPhotos && (kit.photos?.length ?? 0) === 0) {
        return 3;
    }
    // After about (and optional photos), continue at socials unless later steps were already saved.
    if (!hasLaterThanPhotos) {
        return 4;
    }
    if (
        (kit.selectedInterests?.length ?? 0) === 0 &&
        !kit.customInterests?.trim() &&
        !kit.introVideoUrl?.trim() &&
        (kit.highlights?.length ?? 0) === 0
    ) {
        return 5;
    }
    if (!kit.introVideoUrl?.trim() && (kit.highlights?.length ?? 0) === 0) {
        return 6;
    }
    if ((kit.highlights?.length ?? 0) === 0) {
        return 7;
    }
    return 8;
}

export function mediaKitWizardStepStorageKey(athleteUserId?: number): string {
    return `polaris.mediaKit.wizardStep.${athleteUserId ?? 'self'}`;
}

/** Maps API media kit data into wizard form fields (pre-populates from profile-backed fields). */
export function mapMediaKitToFormState(kit: MediaKitData): {
    graduationYear: string;
    aboutYou: string;
    socials: MediaKitSocialFormState;
    introVideoUrl: string;
    customInterests: string;
} {
    return {
        graduationYear: kit.graduationYear?.toString() ?? '',
        aboutYou: kit.aboutYou ?? '',
        socials: mapSocialsToFormState(kit.socials),
        introVideoUrl: kit.introVideoUrl ?? '',
        customInterests: kit.customInterests ?? '',
    };
}
